import { useEffect, useRef, useCallback, useState } from 'react';


const useSessionTimeout = ({
    timeoutMs = 20 * 60 * 1000, // 20 minutes
    warningMs = 15 * 60 * 1000, // 15 minutes
    onWarning,
    onTimeout,
}) => {
    // State to track if the warning should be shown
    const [isWarning, setIsWarning] = useState(false);

    // Refs to store timer IDs so they can be cleared
    const timeoutId = useRef(null);
    const warningId = useRef(null);

    // Refs for callbacks to prevent re-creating timers when callbacks change
    const onTimeoutRef = useRef(onTimeout);
    const onWarningRef = useRef(onWarning);

    // Update refs when callbacks change
    useEffect(() => {
        onTimeoutRef.current = onTimeout;
        onWarningRef.current = onWarning;
    }, [onTimeout, onWarning]);

    // Function to ensure timers are cleared
    const clearTimers = useCallback(() => {
        if (timeoutId.current) clearTimeout(timeoutId.current);
        if (warningId.current) clearTimeout(warningId.current);
    }, []);

    // Function to reset the timers (called on user activity)
    const resetTimer = useCallback(() => {
        setIsWarning(false);
        clearTimers();

        // Set the warning timer
        warningId.current = setTimeout(() => {
            setIsWarning(true);
            if (onWarningRef.current) onWarningRef.current();
        }, warningMs);

        // Set the logout timer
        timeoutId.current = setTimeout(() => {
            if (onTimeoutRef.current) onTimeoutRef.current();
        }, timeoutMs);
    }, [clearTimers, timeoutMs, warningMs]);

    useEffect(() => {
        const events = ['mousedown', 'keydown', 'scroll', 'click', 'keypress'];

        // Throttled handler to prevent excessive function calls on mousemove
        let throttled = false;
        const throttledHandler = () => {
            if (!throttled) {
                resetTimer();
                throttled = true;
                setTimeout(() => { throttled = false }, 1000); // Throttle for 1 second
            }
        }

        // Attach event listeners for user activity
        events.forEach(event => window.addEventListener(event, throttledHandler));

        // Initialize the timer
        resetTimer();

        // Cleanup event listeners and timers on unmount
        return () => {
            events.forEach(event => window.removeEventListener(event, throttledHandler));
            clearTimers();
        };
    }, [resetTimer, clearTimers]);

    return { isWarning, resetTimer };
};

export default useSessionTimeout;
